import { isFindingOpen } from '@qms360/shared';
import { useCan } from '../../lib/session.js';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import {
  Absent,
  CAR_STATUS_LABEL,
  CLASSIFICATION_LABEL,
  Pill,
  carTone,
  classificationTone,
} from '../../components/audit/AuditVocabulary.js';
import { ApiError, describeError } from '../../lib/api.js';
import { fetchReference } from '../../lib/lesson-api.js';
import {
  approveCarExtension,
  fetchCorrectiveActions,
  fetchFindingsLog,
  fetchAuditPeople,
  issueCorrectiveActions,
  requestCarExtension,
  submitCorrectiveAction,
  updateCorrectiveAction,
  verifyCorrectiveAction,
  type CorrectiveAction,
  type FindingClassification,
  type FindingLogRow,
} from '../../lib/audit-api.js';
import { localName, t } from '../../lib/i18n.js';

/**
 * The Findings Log and the CAR workflow (spec §11 key screens 8 and 9).
 *
 * The log is the screen somebody opens to answer "what is still outstanding",
 * and the corrective actions hang off the finding they came from — which is the
 * only arrangement that makes ADR-0012 legible. A flat list of CARs would show
 * two rows for one non-conformity with nothing to say why.
 *
 * **Open findings and open corrective actions are different numbers**, and the
 * counts below are labelled so nobody reads one as the other.
 */
export function FindingsLog() {
  const [params, setParams] = useSearchParams();
  const [expanded, setExpanded] = useState<string | null>(null);

  const filters = {
    auditId: params.get('auditId') ?? undefined,
    classification: (params.get('classification') as FindingClassification | null) ?? undefined,
    state: (params.get('state') as 'open' | 'closed' | null) ?? undefined,
  };

  const log = useQuery({
    queryKey: ['findings-log', filters],
    queryFn: () => fetchFindingsLog(filters),
  });

  const rows: FindingLogRow[] = log.data?.findings ?? [];
  const openCount = rows.filter((row) => isFindingOpen(row)).length;

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <div className="min-h-full">
      <AppTopBar app="audit" />
      <Breadcrumbs app="audit" trail={['Findings log']} />

      <main className="mx-auto max-w-5xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Findings log")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {rows.length}{' '}{t("finding")}{rows.length === 1 ? '' : 's'}{' '}{t("in scope,")}{' '}{openCount}{' '}{t("still open. A finding closes only when every one of its corrective actions is verified effective.")}
        </p>

        <div className="mt-4 flex flex-wrap gap-3">
          <label className="text-sm">
            <span className="me-2 text-content-secondary">{t("Classification")}</span>
            <select
              className="rounded border border-border px-2 py-1"
              value={params.get('classification') ?? ''}
              onChange={(event) => setFilter('classification', event.target.value)}
            >
              <option value="">{t("All")}</option>
              {(Object.keys(CLASSIFICATION_LABEL) as FindingClassification[]).map((key) => (
                <option key={key} value={key}>
                  {t(CLASSIFICATION_LABEL[key])}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="me-2 text-content-secondary">{t("State")}</span>
            <select
              className="rounded border border-border px-2 py-1"
              value={params.get('state') ?? ''}
              onChange={(event) => setFilter('state', event.target.value)}
            >
              <option value="">{t("All")}</option>
              <option value="open">{t("Open")}</option>
              <option value="closed">{t("Closed")}</option>
            </select>
          </label>
        </div>

        {log.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}

        {!log.isLoading && rows.length === 0 && (
          <p className="mt-6 rounded-md border border-border bg-surface p-6 text-content-secondary">
            {t("No findings match these filters.")}
          </p>
        )}

        <ul className="mt-6 list-none space-y-4 p-0">
          {rows.map((finding) => (
            <li
              key={finding.id}
              className="rounded-md border border-border bg-surface p-5 shadow-card"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="m-0 text-base font-semibold">{finding.referenceNo}</h2>
                <span className="flex gap-2">
                  <Pill tone={classificationTone(finding.classification)}>
                    {t(CLASSIFICATION_LABEL[finding.classification])}
                  </Pill>
                  <Pill tone={isFindingOpen(finding) ? 'warning' : 'positive'}>
                    {isFindingOpen(finding) ? t("Open") : finding.closedAt ? t("Closed") : t("No action needed")}
                  </Pill>
                </span>
              </div>

              <p className="mt-1 text-sm text-content-secondary">
                {finding.auditReferenceNo} · {finding.auditTitle}
                {finding.clause ? t(" · clause {0}", { 0: finding.clause }) : ''}
              </p>
              <p className="mt-2 mb-0 text-sm">{finding.description}</p>
              <p className="mt-1 mb-0 text-sm text-content-secondary">
                {t("Risk:")}{' '}
                {finding.riskLevel ?? (
                  <Absent reason={t("Only a non-conformity carries a risk level.")} />
                )}
              </p>

              <button
                type="button"
                className="mt-3 rounded border border-border px-3 py-1.5 text-sm"
                onClick={() => setExpanded(expanded === finding.id ? null : finding.id)}
              >
                {expanded === finding.id ? t("Hide") : t("Corrective actions")}
              </button>

              {expanded === finding.id && <CorrectiveActions finding={finding} />}
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

function CorrectiveActions({ finding }: { finding: FindingLogRow }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [departmentId, setDepartmentId] = useState('');
  /** Who owns the CAR in that department (FN-10); optional. */
  const [ownerUserId, setOwnerUserId] = useState('');
  // Issuing is the audit team's; a department owner sees their CARs, not the form (pass 9).
  const canIssue = useCan('audit', 'create_edit');
  const people = useQuery({ queryKey: ['audit-people'], queryFn: fetchAuditPeople, retry: false, enabled: canIssue });

  const cars = useQuery({
    queryKey: ['corrective-actions', finding.id],
    queryFn: () => fetchCorrectiveActions(finding.id),
  });
  const reference = useQuery({ queryKey: ['reference', 'audit'], queryFn: () => fetchReference('audit') });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['corrective-actions', finding.id] });
    void queryClient.invalidateQueries({ queryKey: ['findings-log'] });
  };

  const fail = (fallback: string) => (caught: unknown) =>
    setError(describeError(caught, fallback));

  const issue = useMutation({
    mutationFn: () => issueCorrectiveActions(finding.id, [{ businessUnitId: departmentId, ownerUserId: ownerUserId || null }]),
    onSuccess: () => {
      setError(null);
      setDepartmentId('');
      setOwnerUserId('');
      refresh();
    },
    onError: fail(t("That corrective action could not be issued.")),
  });

  const rows: CorrectiveAction[] = cars.data?.correctiveActions ?? [];
  // A department has a CURRENT corrective action unless its only ones were
  // verified not effective — then it needs a follow-up (DATA-8).
  const current = new Set(
    rows.filter((car) => car.status !== 'closed_not_effective').map((car) => car.responsibleBusinessUnitId),
  );
  const needsFollowUp = new Set(
    rows
      .filter((car) => car.status === 'closed_not_effective' && !current.has(car.responsibleBusinessUnitId))
      .map((car) => car.responsibleBusinessUnitId),
  );
  const departments = (reference.data?.businessUnits ?? []).filter((unit) => !current.has(unit.id));
  const referenceOf = new Map(rows.map((car) => [car.id, car.referenceNo]));

  const isNonConformity =
    finding.classification === 'minor_nc' || finding.classification === 'major_nc';

  return (
    <div className="mt-4 border-t border-border pt-4">
      {error && (
        <p role="alert" className="mb-3 text-sm" style={{ color: 'var(--tone-rejected)' }}>
          {error}
        </p>
      )}

      {!isNonConformity ? (
        <p className="m-0 text-sm text-content-secondary">
          A {CLASSIFICATION_LABEL[finding.classification].toLowerCase()}{' '}{t("is a record, not an action: it carries no corrective action and needs no closing.")}
        </p>
      ) : (
        <>
          <p className="m-0 text-sm text-content-secondary">
            {t("One corrective action per responsible department, each with its own owner, deadline and closure — so a non-conformity spanning two departments is tracked as two.")}
          </p>

          {!finding.closedAt && departments.length > 0 && canIssue && (
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <label className="text-sm">
                <span className="me-2 text-content-secondary">{t("Responsible department")}</span>
                <select
                  className="rounded border border-border px-2 py-1"
                  value={departmentId}
                  onChange={(event) => setDepartmentId(event.target.value)}
                >
                  <option value="">{t("Choose…")}</option>
                  {departments.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {localName(unit)}
                      {needsFollowUp.has(unit.id) ? t(" — follow-up (last one not effective)") : ''}
                    </option>
                  ))}
                </select>
              </label>
              {(people.data?.people.length ?? 0) > 0 && (
                <label className="text-sm">
                  <span className="me-2 text-content-secondary">{t("Owner (optional)")}</span>
                  <select
                    className="rounded border border-border px-2 py-1"
                    value={ownerUserId}
                    onChange={(event) => setOwnerUserId(event.target.value)}
                  >
                    <option value="">{t("The department decides")}</option>
                    {people.data!.people.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.displayName}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <button
                type="button"
                className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white"
                disabled={!departmentId || issue.isPending}
                onClick={() => issue.mutate()}
              >
                {needsFollowUp.has(departmentId) ? t("Issue follow-up") : t("Issue")}
              </button>
            </div>
          )}

          <ul className="mt-4 list-none space-y-3 p-0">
            {rows.map((car) => (
              <CarCard
                key={car.id}
                car={car}
                followUpOf={car.supersedesCarId ? (referenceOf.get(car.supersedesCarId) ?? null) : null}
                onChanged={refresh}
                onError={setError}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function CarCard({
  car,
  followUpOf,
  onChanged,
  onError,
}: {
  car: CorrectiveAction;
  /** The ineffective CAR this one follows up, by reference. */
  followUpOf: string | null;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [rootCause, setRootCause] = useState(car.rootCause);
  const [correction, setCorrection] = useState(car.correction);
  const [action, setAction] = useState(car.correctiveAction);
  const [target, setTarget] = useState(car.targetCompletionDate ?? '');
  const [notes, setNotes] = useState('');
  const [extensionUntil, setExtensionUntil] = useState('');
  const [extensionReason, setExtensionReason] = useState('');

  const fail = (fallback: string) => (caught: unknown) =>
    onError(describeError(caught, fallback));

  const save = useMutation({
    mutationFn: () =>
      updateCorrectiveAction(car.id, {
        rootCause,
        correction,
        correctiveAction: action,
        targetCompletionDate: target || null,
      }),
    onSuccess: onChanged,
    onError: fail(t("That could not be saved.")),
  });

  const submit = useMutation({
    mutationFn: () => submitCorrectiveAction(car.id),
    onSuccess: onChanged,
    onError: fail(t("That could not be submitted.")),
  });

  const verify = useMutation({
    mutationFn: (effective: boolean) => verifyCorrectiveAction(car.id, { effective, notes }),
    onSuccess: onChanged,
    onError: fail(t("That could not be verified.")),
  });

  const extend = useMutation({
    mutationFn: () =>
      requestCarExtension(car.id, { until: extensionUntil, reason: extensionReason }),
    onSuccess: onChanged,
    onError: fail(t("That extension could not be requested.")),
  });

  const approveExtension = useMutation({
    mutationFn: () => approveCarExtension(car.id),
    onSuccess: onChanged,
    onError: fail(t("That extension could not be approved.")),
  });

  const editable = car.status === 'issued' || car.status === 'in_progress';
  const awaitingVerification = car.status === 'submitted' || car.status === 'verifying';

  return (
    <li className="rounded-md border border-border p-4 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <strong>{car.referenceNo}</strong>
        <Pill tone={carTone(car.status)}>{t(CAR_STATUS_LABEL[car.status])}</Pill>
      </div>
      {followUpOf && (
        <p className="mb-0 mt-1 text-xs text-content-secondary">{t("Follow-up of")}{' '}{followUpOf}{t(", which was not effective.")}</p>
      )}

      {editable ? (
        <form
          className="mt-3 grid gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <label className="flex flex-col">
            <span className="mb-1 text-content-secondary">{t("Root cause — what went wrong")}</span>
            <textarea
              className="rounded border border-border px-2 py-1"
              rows={2}
              value={rootCause}
              onChange={(event) => setRootCause(event.target.value)}
            />
          </label>
          <label className="flex flex-col">
            <span className="mb-1 text-content-secondary">
              {t("Correction — what was done about this instance")}
            </span>
            <textarea
              className="rounded border border-border px-2 py-1"
              rows={2}
              value={correction}
              onChange={(event) => setCorrection(event.target.value)}
            />
          </label>
          <label className="flex flex-col">
            <span className="mb-1 text-content-secondary">
              {t("Corrective action — what stops it happening again")}
            </span>
            <textarea
              className="rounded border border-border px-2 py-1"
              rows={2}
              value={action}
              onChange={(event) => setAction(event.target.value)}
            />
          </label>
          <label className="flex flex-col">
            <span className="mb-1 text-content-secondary">{t("Target completion")}</span>
            <input
              type="date"
              className="w-48 rounded border border-border px-2 py-1"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
            />
          </label>

          <div className="flex flex-wrap gap-2">
            <button type="submit" className="rounded border border-border px-3 py-1.5">
              {t("Save")}
            </button>
            <button
              type="button"
              className="rounded bg-ag-purple px-3 py-1.5 text-white"
              disabled={submit.isPending}
              onClick={() => submit.mutate()}
            >
              {t("Submit for verification")}
            </button>
          </div>
        </form>
      ) : (
        <dl className="mt-3 grid gap-1">
          <div>
            <dt className="inline text-content-secondary">{t("Root cause:")}{' '}</dt>
            <dd className="inline">{car.rootCause || <Absent reason={t("Not recorded.")} />}</dd>
          </div>
          <div>
            <dt className="inline text-content-secondary">{t("Corrective action:")}{' '}</dt>
            <dd className="inline">
              {car.correctiveAction || <Absent reason={t("Not recorded.")} />}
            </dd>
          </div>
          {car.verificationNotes && (
            <div>
              <dt className="inline text-content-secondary">{t("Verification:")}{' '}</dt>
              <dd className="inline">{car.verificationNotes}</dd>
            </div>
          )}
        </dl>
      )}

      <p className="mt-2 mb-0 text-content-secondary">
        {t("Due")}{' '}{car.targetCompletionDate ?? '—'}
        {car.extensionRequestedUntil && (
          <>
            {t(" · extension requested to ")}
            {car.extensionRequestedUntil}
            {car.extensionApprovedAt ? t(" (approved)") : t(" — not approved, so the date stands")}
          </>
        )}
      </p>

      {awaitingVerification && (
        <div className="mt-3 rounded border border-border bg-tone-info-surface p-3">
          <label className="flex flex-col">
            <span className="mb-1 text-content-secondary">
              {t("What you verified — required either way")}
            </span>
            <textarea
              className="rounded border border-border px-2 py-1"
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded bg-ag-purple px-3 py-1.5 text-white"
              disabled={!notes.trim() || verify.isPending}
              onClick={() => verify.mutate(true)}
            >
              {t("Effective — close it")}
            </button>
            <button
              type="button"
              className="rounded border border-border px-3 py-1.5"
              disabled={!notes.trim() || verify.isPending}
              onClick={() => verify.mutate(false)}
            >
              {t("Not effective")}
            </button>
          </div>
          <p className="mb-0 mt-2 text-xs text-content-secondary">
            {t("“Not effective” is a finished corrective action that did not work. It closes this one and leaves the finding open for a follow-up.")}
          </p>
        </div>
      )}

      {car.extensionRequestedUntil && !car.extensionApprovedAt && (
        <button
          type="button"
          className="mt-3 rounded border border-border px-3 py-1.5"
          disabled={approveExtension.isPending}
          onClick={() => approveExtension.mutate()}
        >
          {t("Approve the extension")}
        </button>
      )}

      {/* The extension has its OWN date and reason. An earlier version borrowed
          the target-date input and the verification notes, which meant the
          reason field only existed while somebody else was verifying — so the
          button could never be enabled. */}
      {editable && (
        <form
          className="mt-3 border-t border-border pt-3"
          onSubmit={(event) => {
            event.preventDefault();
            extend.mutate();
          }}
        >
          <p className="m-0 text-content-secondary">
            {t("Request an extension. It changes nothing until the Audit Lead approves it — the current date stands until then.")}
          </p>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <label className="flex flex-col">
              <span className="mb-1 text-content-secondary">{t("New date")}</span>
              <input
                type="date"
                className="rounded border border-border px-2 py-1"
                value={extensionUntil}
                onChange={(event) => setExtensionUntil(event.target.value)}
              />
            </label>
            <label className="flex flex-1 flex-col">
              <span className="mb-1 text-content-secondary">{t("Why")}</span>
              <input
                className="rounded border border-border px-2 py-1"
                value={extensionReason}
                onChange={(event) => setExtensionReason(event.target.value)}
              />
            </label>
            <button
              type="submit"
              className="rounded border border-border px-3 py-1.5"
              disabled={!extensionUntil || !extensionReason.trim() || extend.isPending}
            >
              {t("Request extension")}
            </button>
          </div>
        </form>
      )}
    </li>
  );
}
